import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ShoppingBag, Edit2, Trash2, Plus, X, ShieldCheck, Upload, Tag } from 'lucide-react';
import { safeConfirm } from '../lib/confirm';

/**
 * Product Catalogue — same component used at /store (any logged-in user)
 * and /admin/store (admin sees inventory + add/edit). Patients, clinicians,
 * reception see the public catalogue with country-tax-aware retail price.
 */
export default function Storefront() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [edit, setEdit] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const load = () => api.get('/store/products').then(r => setProducts(r.data));
  useEffect(() => {
    load();
    api.get('/store/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const filtered = filterCategory === 'all'
    ? products
    : products.filter(p => p.category === filterCategory);

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CLINIC STORE</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Product Catalogue</h1>
          <p className="text-fg-muted mt-2">Retail prices include local tax. {products[0]?.country ? `Country: ${products[0].country}` : ''}</p>
        </div>
        {isAdmin && (
          <button onClick={()=>setEdit({ visible: true, currency: 'PLN', stock: 0, price: 0, discount_percent: 0 })} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="new-store-product-btn">
            <Plus className="w-4 h-4"/> New product
          </button>
        )}
      </div>

      {/* Category filter */}
      {categories.length > 0 && (
        <div className="mb-5 flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-fg-muted mr-1">Category</span>
          <button onClick={()=>setFilterCategory('all')}
                  className={`text-xs px-3 py-1.5 rounded-full border ${filterCategory==='all' ? 'bg-brand text-white border-brand' : 'border-soft text-fg-muted hover:border-fg/30'}`}
                  data-testid="cat-filter-all">
            All
          </button>
          {categories.map(c => (
            <button key={c} onClick={()=>setFilterCategory(c)}
                    className={`text-xs px-3 py-1.5 rounded-full border ${filterCategory===c ? 'bg-brand text-white border-brand' : 'border-soft text-fg-muted hover:border-fg/30'}`}
                    data-testid={`cat-filter-${c.replace(/\s+/g,'-').toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>
      )}

      {!filtered.length ? (
        <div className="surface border border-soft rounded-lg p-12 text-center">
          <ShoppingBag className="w-12 h-12 mx-auto text-fg-muted"/>
          <p className="text-sm text-fg-muted mt-4">No products in this category yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <div key={p.product_id} className="surface border border-soft rounded-xl overflow-hidden flex flex-col" data-testid={`store-product-${p.product_id}`}>
              <div className="h-44 bg-soft overflow-hidden flex items-center justify-center">
                {p.image_url
                  ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover"/>
                  : <ShoppingBag className="w-10 h-10 text-fg-muted/40"/>}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading font-semibold">{p.name}</h3>
                  {!p.visible && <span className="badge-neutral text-[10px]">hidden</span>}
                </div>
                {p.category && <div className="text-[11px] text-fg-muted mt-1 uppercase tracking-wider inline-flex items-center gap-1"><Tag className="w-3 h-3"/>{p.category}</div>}
                {p.description && <p className="text-sm text-fg-muted mt-2 line-clamp-2 flex-1">{p.description}</p>}
                <div className="mt-3 flex items-end justify-between border-t border-soft pt-3">
                  <div>
                    {p.discount_percent > 0 && (
                      <div className="text-xs line-through text-fg-muted">
                        {(p.price * (1 + (p.effective_vat_rate || 0) / 100)).toFixed(2)} {p.currency}
                      </div>
                    )}
                    <div className="text-2xl font-heading font-bold text-brand">{p.price_with_tax.toFixed(2)} {p.currency}</div>
                    <div className="text-[10px] text-fg-muted flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3"/> incl. {p.effective_vat_rate}% tax
                      {p.discount_percent > 0 && <span className="ml-1 text-[#B83A3A] font-medium">· -{p.discount_percent}% OFF</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button onClick={()=>setEdit(p)} className="text-brand p-1" data-testid={`edit-store-${p.product_id}`}><Edit2 className="w-3.5 h-3.5"/></button>
                      <button onClick={async ()=>{
                        if (!await safeConfirm(`Delete ${p.name}?`)) return;
                        await api.delete(`/store/products/${p.product_id}`); load();
                      }} className="text-[#B83A3A] p-1" data-testid={`del-store-${p.product_id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  )}
                </div>
                {!isAdmin && p.stock > 0 && (
                  <div className="text-[11px] text-fg-muted mt-2">{p.stock} in stock</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <EditModal product={edit} categories={categories} onClose={()=>setEdit(null)} onSaved={()=>{ load(); setEdit(null); }}/>}
    </div>
  );
}

function EditModal({ product, categories, onClose, onSaved }) {
  const isNew = !product.product_id;
  const [form, setForm] = useState({ name: '', description: '', sku: '', image_url: '',
                                       price: 0, currency: 'PLN', vat_rate: null, discount_percent: 0,
                                       stock: 0, visible: true, category: '', ...product });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image too large — max 5MB.'); return; }
    const reader = new FileReader();
    reader.onload = (e) => set('image_url', e.target.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form,
                      price: parseFloat(form.price) || 0,
                      stock: parseInt(form.stock) || 0,
                      discount_percent: parseFloat(form.discount_percent) || 0,
                      vat_rate: form.vat_rate === '' ? null : (form.vat_rate === null ? null : parseFloat(form.vat_rate)) };
      if (isNew) await api.post('/store/products', body);
      else await api.put(`/store/products/${product.product_id}`, body);
      onSaved();
    } catch (e) { alert('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="store-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center justify-between">
          <h2 className="font-heading font-semibold">{isNew ? 'New product' : `Edit · ${product.name}`}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-3">
          <Field label="Name *" full><input className="input" value={form.name} onChange={e=>set('name', e.target.value)} data-testid="sp-name"/></Field>

          {/* Image upload + preview */}
          <Field label="Product picture" full>
            <div className="flex items-start gap-3">
              <div className="w-28 h-28 rounded-lg border border-soft bg-soft flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.image_url
                  ? <img src={form.image_url} alt="" className="w-full h-full object-cover"/>
                  : <ShoppingBag className="w-8 h-8 text-fg-muted/40"/>}
              </div>
              <div className="flex-1">
                <label className="btn-secondary text-xs inline-flex items-center gap-2 cursor-pointer" data-testid="sp-upload-image">
                  <Upload className="w-3.5 h-3.5"/> Upload picture
                  <input type="file" accept="image/*" hidden onChange={e => handleImage(e.target.files?.[0])}/>
                </label>
                <p className="text-[11px] text-fg-muted mt-2">PNG/JPG/WebP, max 5 MB. Or paste a URL below.</p>
                <input className="input mt-2 text-xs" value={form.image_url || ''} onChange={e=>set('image_url', e.target.value)} placeholder="https://… or data:…" data-testid="sp-image-url"/>
                {form.image_url && (
                  <button onClick={() => set('image_url', '')} className="text-[11px] text-[#B83A3A] mt-1">Remove image</button>
                )}
              </div>
            </div>
          </Field>

          <Field label="Category" full>
            <select className="input" value={form.category || ''} onChange={e=>set('category', e.target.value)} data-testid="sp-category">
              <option value="">— select category —</option>
              {(categories || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="SKU"><input className="input" value={form.sku||''} onChange={e=>set('sku', e.target.value)}/></Field>
          <Field label="Stock"><input className="input" type="number" value={form.stock} onChange={e=>set('stock', e.target.value)} data-testid="sp-stock"/></Field>

          <Field label="Price (net)"><input className="input" type="number" step="0.01" value={form.price} onChange={e=>set('price', e.target.value)} data-testid="sp-price"/></Field>
          <Field label="Currency"><select className="input" value={form.currency} onChange={e=>set('currency', e.target.value)}><option>PLN</option><option>EUR</option><option>USD</option><option>CAD</option><option>GBP</option></select></Field>

          <Field label="Discount %">
            <input className="input" type="number" min="0" max="100" step="1" value={form.discount_percent || 0} onChange={e=>set('discount_percent', e.target.value)} data-testid="sp-discount" placeholder="0"/>
          </Field>
          <Field label="VAT rate % (blank = use country default)">
            <input className="input" type="number" step="0.1" value={form.vat_rate ?? ''} onChange={e=>set('vat_rate', e.target.value === '' ? null : e.target.value)}/>
          </Field>

          <Field label="Description" full><textarea className="input min-h-[80px]" value={form.description||''} onChange={e=>set('description', e.target.value)}/></Field>
          <label className="md:col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.visible} onChange={e=>set('visible', e.target.checked)}/> Visible to non-admin viewers
          </label>
        </div>
        <div className="px-5 pb-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !form.name} className="btn-primary text-sm" data-testid="sp-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return <label className={full ? 'md:col-span-2 block' : 'block'}>
    <div className="text-xs text-fg-muted mb-1">{label}</div>{children}
  </label>;
}
